import { withRouter } from 'next/router';
import Head from 'next/head';
import { arrayOf, bool, node, shape, string, func } from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { setPageType } from 'actions/page';
import { clearSurrogateKey } from 'actions/app';
import classnames from 'classnames';
import dynamic from 'next/dynamic';
import Ad from 'components/Ad';
import SiteHeader from 'components/SiteHeader';
import SiteFooter from 'components/SiteFooter';
import ThirdParty from 'components/ThirdParty';
import SprinkleBundle from 'components/SprinkleBundle';
import Metadata from 'components/Metadata';
import Sailthru from 'components/Sailthru';
import { CATEGORY, COLLECTION } from 'constants/pageTypes';
import { BASE_AD_UNITS } from 'constants/ads/units';
import { CUSTOM_CREATIVE_SIZE } from 'constants/ads/sizes';
import { registerPageview } from 'util/analytics';
import { getUserFromCookie } from 'util/auth';
import { getBundle } from 'util/bundles';
import { setCookie } from 'util/originatingSource';
import { getCleanPath } from 'util/link';
import {
  createMetadataObject,
  extractMetadataFromBundleItems
} from 'util/metadata';
import { generateMetadataBundleItems } from 'util/metadataBundle';
import { getSiteHeaderSettings } from 'util/header';
import { getFormattedDate } from 'oxygen-lib-ui/helpers/date';

const GdprBannerController = dynamic(
  () => import('components/GdprBannerController'),
  {
    ssr: false,
    loading: () => null
  }
);

export const LOADING_METADATA_BUNDLE_ITEMS = generateMetadataBundleItems(
  createMetadataObject({
    title: 'Loading...'
  })
);

// CLEANUP: handle page/route based classes in a nicer way
const ROUTE_CLASSES = {
  '/': 'home collection',
  '/authors/show': 'authors',
  '/categories/show': 'category collection',
  '/tags/show': 'collection',
  '/post/show': 'post-page',
  '/search': 'search',
  '/page': 'static',
  '/homemade/show': 'homemade',
  // We need to add this classname to Workflow preview pages
  // for certain bundles to render properly
  // Recipe preview also uses this path, but the addition of the
  // classname shouldn't interfere
  '/admin/preview': 'collection'
};

export class App extends Component {
  constructor(props) {
    super(props);

    const { pageType } = props;

    if (pageType) {
      props.setPageType(pageType);
    }
  }

  componentDidMount() {
    if (process.browser) {
      setCookie(document.referrer);
    }

    // only register a page load on mount if we have pageBundles, which
    // indicates that we have content to render. this condition handles cases
    // where page bundles are effectively cached (rehydrating from server state
    // or a previous client-side render).
    if (this.props.pageBundles) {
      this.registerPageLoad();
    }
  }

  componentDidUpdate(prevProps) {
    const { pageType, pageBundles, router } = this.props;
    const {
      pageType: prevPageType,
      pageBundles: prevPageBundles,
      router: { asPath: prevAsPath }
    } = prevProps;

    // keep page type up-to-date in redux store
    if (prevPageType !== pageType) {
      this.props.setPageType(pageType);
    }

    // Has the component transitioned out of the loading state? (we now have
    // pageBundles)
    const isFinishedLoading = !prevPageBundles && !!pageBundles;

    // has a client-side routing event occurred?
    const hasPathChanged = prevAsPath !== router.asPath;

    // Register a page load if this component has moved from the loading state,
    // or if the route has changed and there is cached data
    const shouldRegisterPageLoad =
      isFinishedLoading || (hasPathChanged && pageBundles);

    if (shouldRegisterPageLoad) {
      this.registerPageLoad();
    }
  }

  registerPageLoad() {
    const {
      router,
      pageBundles,
      pageType,
      httpStatus,
      flips,
      ssrRenderDatetime,
      deployId,
      surrogateKey
    } = this.props;

    try {
      const metadataBundle = getBundle(pageBundles, 'metadata');

      if (!metadataBundle) {
        throw new Error(
          `Missing metadata bundle for '${router.asPath}'; Cannot call registerPageview()`
        );
      }

      const pageMetadata = extractMetadataFromBundleItems(metadataBundle.items);

      // CLEANUP: it would be nice to just update the redux store here instead
      // of explicitly registering a pageview event.
      //
      // CLEANUP: refactor args to be an object
      registerPageview(
        router.asPath,
        pageMetadata,
        pageType,
        httpStatus,
        flips,
        ssrRenderDatetime,
        deployId,
        surrogateKey
      );
    } catch (e) {
      console.error(e.message); // eslint-disable-line no-console
    }

    // the "signed-in" query param does not make it to the origin due to a
    // block/allow list in fastly. that means we have to access the query param
    // via the window.location object instead of next's router. sigh.
    if (
      typeof window !== 'undefined' &&
      window.location &&
      window.location.search.indexOf('signed-in=') > -1
    ) {
      const user = getUserFromCookie();
      if (user && user.email) {
        // sailthru app timezone is America/New_York, so pass date in that
        // timezone, not the reader's.
        const isoLoginDate = getFormattedDate(
          new Date().valueOf() / 1000,
          '%Y-%m-%d'
        );

        Sailthru.addUpdateUser(user.email, {
          website_last_login_date: isoLoginDate
        });
      }
    }

    // clear the surrogate key value after a pageview because it's no longer
    // valid for additional client-side routing pageview calls.
    this.props.clearSurrogateKey();
  }

  render() {
    const {
      children,
      router,
      pageBundles,
      pageType,
      setNoSkim,
      showNav
    } = this.props;

    // CLEANUP: Assuming here that asPath doesn't always exist. If the
    // assumption is wrong, we don't need the condition
    const currentPath = router.asPath
      ? getCleanPath(router.asPath)
      : router.pathname;

    // shared recipe box tag pages that are a 404 should render standard header variant
    const { variant } =
      pageType === 'error404' ? '' : getSiteHeaderSettings(currentPath);

    const { logo } = getSiteHeaderSettings(currentPath);

    const sprinkleBundle = getBundle(pageBundles, 'sprinkle');
    const header =
      sprinkleBundle &&
      sprinkleBundle.items &&
      sprinkleBundle.items.find(s => s.type === 'header');
    const headerColor =
      header && header.data && header.data.color ? header.data.color : null;

    const metadataBundle = getBundle(pageBundles, 'metadata');
    const metadataBundleItems =
      metadataBundle && metadataBundle.items
        ? metadataBundle.items
        : LOADING_METADATA_BUNDLE_ITEMS;

    // CLEANUP: we could move this logic into the config in future
    /**
     * We are taking advantage that every Category and Collection page is
     * including an empty header (with title = null). And we use that header to
     * set the default title for those pages, to include a h1 tag on every index
     * page.
     *
     * Only if the Title isn't explicity defined for the Category or Collection
     * page, we should set it up for SEO purposes
     */
    if (
      header &&
      !header.data.title &&
      (pageType === CATEGORY || pageType === COLLECTION)
    ) {
      // Lookup for the Title provided in the Metadata
      const metadata = extractMetadataFromBundleItems(metadataBundle.items);
      header.data = {
        ...header.data,
        title: metadata.title,
        color: 'white'
      };
    }

    const classes = classnames(
      ROUTE_CLASSES[router.pathname],
      // CLEANUP: This css pattern is deprecated, but wait a bit before taking
      // it out until we're sure we don't need it
      currentPath.substring(1),
      {
        [`theme--${headerColor}`]: !!headerColor,
        // Disable skimlinks by default
        // Post, FAQ and other affiliate pages will:
        // - Conditionally load Skimlinks if the page is an affiliate page, or
        // - Set noskim if the page is NOT and affiliate page
        'noskim noskimwords': setNoSkim
      }
    );

    return (
      <Fragment>
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, shrink-to-fit=no, minimum-scale=1"
          />
        </Head>
        <Metadata items={metadataBundleItems} />
        <ThirdParty />

        {BASE_AD_UNITS.universal && (
          <div className="App__ad">
            <Ad
              id="dfp-universal-banner"
              unit={BASE_AD_UNITS.universal}
              sizes={[CUSTOM_CREATIVE_SIZE]}
            />
          </div>
        )}

        <SiteHeader
          showNav={showNav}
          logo={logo}
          variant={variant}
          currentPath={currentPath}
        />

        <main className={classes} data-page-path={currentPath}>
          {sprinkleBundle && <SprinkleBundle items={sprinkleBundle.items} />}

          {children}
        </main>
        <GdprBannerController />
        <SiteFooter />
      </Fragment>
    );
  }
}

App.defaultProps = {
  pageBundles: undefined,
  pageType: undefined,
  flips: [],
  ssrRenderDatetime: undefined,
  deployId: undefined,
  surrogateKey: undefined,
  httpStatus: '200: OK',
  setNoSkim: true,
  showNav: true
};

App.propTypes = {
  children: node.isRequired,
  pageBundles: arrayOf(
    shape({
      type: string
    })
  ),
  pageType: string,
  flips: arrayOf(string),
  ssrRenderDatetime: string,
  deployId: string,
  surrogateKey: string,
  router: shape({
    pathname: string.isRequired,
    query: shape({}).isRequired,
    asPath: string.isRequired
  }).isRequired,
  setPageType: func.isRequired,
  clearSurrogateKey: func.isRequired,
  httpStatus: string,
  setNoSkim: bool,
  showNav: bool
};

const mapStateToProps = ({
  app: { flips, ssrRenderDatetime, deployId, surrogateKey }
}) => ({
  flips,
  // these next props come from the SSR-rendered page via response headers.
  // CLEANUP: we need to decide what to do with them after a client-side
  // routing event.
  ssrRenderDatetime,
  deployId,
  surrogateKey
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ setPageType, clearSurrogateKey }, dispatch);

// CLEANUP: this will be reworked/removed when we move to a proper _app
// component
export const AppWithGlobalState = connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(App));

export default withRouter(App);
